import { useState, useEffect, useRef, useCallback } from 'react'
import { Modal } from '../ui/Modal'
import { useTranslation } from '../../contexts/LanguageContext'

interface DownloadModalProps {
  isOpen: boolean
  onClose: () => void
  onDownload: () => Promise<void>
}

export function DownloadModal({ isOpen, onClose, onDownload }: DownloadModalProps) {
  const { t } = useTranslation()
  const [progress, setProgress] = useState(0)
  const [downloaded, setDownloaded] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasTriggeredDownload = useRef(false)

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Start progress when modal opens
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setProgress(0)
      setDownloaded(false)
      hasTriggeredDownload.current = false
      cleanup()
      return
    }

    // Start the progress timer (~4.5s to reach 100)
    // 90 steps * 50ms = 4500ms
    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) return 100
        // Increment by ~1.1 per tick for ~90 ticks
        return Math.min(prev + 1.12, 100)
      })
    }, 50)

    return cleanup
  }, [isOpen, cleanup])

  // Trigger download when progress reaches 100%
  useEffect(() => {
    if (progress >= 100 && !hasTriggeredDownload.current) {
      hasTriggeredDownload.current = true
      cleanup()
      onDownload().then(() => {
        setDownloaded(true)
      })
    }
  }, [progress, onDownload, cleanup])

  const handleClose = () => {
    cleanup()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      closeOnOverlayClick={downloaded}
    >
      <div className="flex flex-col items-center gap-5 pt-4 pb-2">
        {/* Progress section */}
        <div className="w-full">
          {/* Progress bar */}
          <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-100 ease-linear"
              style={{
                width: `${Math.min(progress, 100)}%`,
                background: downloaded
                  ? '#22c55e'
                  : 'linear-gradient(90deg, #6366f1, #8b5cf6, #a855f7)',
              }}
            />
          </div>

          {/* Status text */}
          <div className="flex items-center justify-center gap-2 mt-3">
            {downloaded ? (
              <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-purple-500 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            <p className={`text-sm font-medium ${downloaded ? 'text-green-600' : 'text-gray-600'}`}>
              {downloaded ? t.downloadModal.ready : t.downloadModal.preparing}
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="w-full border-t border-gray-100" />

        {/* Ko-fi support card */}
        <div className="w-full rounded-xl border border-gray-100 bg-gray-50/50 p-4">
          <div className="flex items-start gap-3">
            {/* Coffee icon */}
            <div
              className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#FFF0F0' }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#FF5E5B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 8h1a4 4 0 110 8h-1" />
                <path d="M3 8h14v9a4 4 0 01-4 4H7a4 4 0 01-4-4V8z" />
                <line x1="6" y1="2" x2="6" y2="4" />
                <line x1="10" y1="2" x2="10" y2="4" />
                <line x1="14" y1="2" x2="14" y2="4" />
              </svg>
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">
                {t.downloadModal.supportTitle}
              </p>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                {t.downloadModal.supportMessage}
              </p>
            </div>
          </div>

          {/* Ko-fi button */}
          <a
            href="https://ko-fi.com/cvtoday"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98]"
            style={{ backgroundColor: '#FF5E5B' }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            {t.downloadModal.supportButton}
          </a>
        </div>
      </div>
    </Modal>
  )
}
